import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import VerifiedIcon from '@mui/icons-material/Verified';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import PhoneIcon from '@mui/icons-material/Phone';
import { useMediaQuery } from '@mui/material';
import SectionTitle from './SectionTitle';

const Section = styled.section`
  background: #f7f7f9;
  min-height: 540px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 0 0 40px 40px;
  box-shadow: 0 4px 32px rgba(44, 62, 80, 0.08);
  margin: 0 auto 2rem auto;
  width: 100%;
  max-width: 100vw;
  position: relative;
  z-index: 2;
  overflow: hidden;
`;

const PawSVG = styled.svg`
  position: absolute;
  opacity: 0.08;
  z-index: 1;
  pointer-events: none;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 5vw;
  width: 80vw;
  max-width: 1200px;
  min-height: 540px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  @media (max-width: 1000px) {
    flex-direction: column;
    gap: 2.5rem;
    min-height: unset;
    width: 100vw;
    padding: 3rem 0;
  }
`;

const PhotoCluster = styled.div`
  position: relative;
  width: 380px;
  height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 700px) {
    width: 100vw;
    height: auto;
    flex-direction: row;
    justify-content: center;
    align-items: flex-end;
    gap: 1.1rem;
    position: static;
    margin-bottom: 1.2rem;
  }
`;

const MainPhoto = styled.img`
  width: 280px;
  height: 280px;
  object-fit: cover;
  border-radius: 50%;
  border: 7px solid #fff;
  box-shadow: 0 8px 32px rgba(44,62,80,0.13);
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  background: #fff;
  transition: transform 0.3s, box-shadow 0.3s;
  &:hover {
    transform: translate(-50%, -50%) scale(1.04);
    box-shadow: 0 16px 48px rgba(44,62,80,0.18);
  }
  @media (max-width: 700px) {
    position: static;
    width: 110px;
    height: 110px;
    border-width: 3px;
    margin: 0;
    transform: none;
    &:hover {
      transform: scale(1.04);
    }
    box-shadow: 0 2px 10px rgba(44,62,80,0.10);
  }
`;

const SmallPhoto = styled.img<{ $pos: 'bottom-right' | 'top-right' }>`
  width: 110px;
  height: 110px;
  object-fit: cover;
  border-radius: 50%;
  border: 5px solid #fff;
  box-shadow: 0 4px 16px rgba(44,62,80,0.13);
  position: absolute;
  z-index: 3;
  left: ${({ $pos }) => ($pos === 'top-right' ? '75%' : '75%')};
  top: ${({ $pos }) => ($pos === 'top-right' ? '15%' : '65%')};
  background: #fff;
  transition: transform 0.3s, box-shadow 0.3s;
  &:hover {
    transform: scale(1.07);
    box-shadow: 0 12px 32px rgba(44,62,80,0.18);
  }
  @media (max-width: 700px) {
    position: static;
    width: 80px;
    height: 80px;
    border-width: 3px;
    margin: 0;
    box-shadow: 0 2px 10px rgba(44,62,80,0.10);
  }
`;

const PhotoLabel = styled.span`
  display: block;
  color: #ff9d5c;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  margin-top: 0.3rem;
  font-family: 'Poppins', sans-serif;
  @media (max-width: 700px) {
    font-size: 0.8rem;
  }
`;

const InfoArea = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 1.5rem;
  min-width: 320px;
  @media (max-width: 1000px) {
    align-items: center;
    text-align: center;
    width: 100%;
    gap: 1.2rem;
    padding: 0 1.5rem;
  }
`;

const BadgeContainer = styled.div`
  display: flex;
  gap: 0.8rem;
  align-items: center;
  flex-wrap: wrap;
  @media (max-width: 1000px) {
    justify-content: center;
  }
`;

const Badge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  color: #ff9d5c;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 30px;
  padding: 0.4rem 1rem;
  box-shadow: 0 2px 8px rgba(255, 157, 92, 0.15);
  width: fit-content;
  margin-top: 0.5rem;
  border: 1px solid rgba(255, 157, 92, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 157, 92, 0.2);
  }

  @media (max-width: 1000px) {
    margin: 0 auto;
    font-size: 0.85rem;
  }
`;

const Description = styled.p`
  color: #222;
  font-size: 1.35rem;
  text-align: left;
  margin-bottom: 0.5rem;
  max-width: 700px;
  font-weight: 400;
  font-style: italic;
  text-shadow: 0 1px 6px rgba(44,62,80,0.06);
  font-family: 'Merriweather', serif;
  line-height: 1.8;
  @media (max-width: 1000px) {
    text-align: center;
    width: 100%;
    font-size: 1.05rem;
    line-height: 1.6;
    padding: 0 0.5rem;
  }
`;

const Highlight = styled.span`
  color: #ff9d5c;
  font-weight: 700;
  font-style: normal;
`;

const LightboxOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LightboxImg = styled.img`
  max-width: 90vw;
  max-height: 80vh;
  border-radius: 18px;
  box-shadow: 0 8px 32px rgba(44,62,80,0.25);
  background: #fff;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  background: #fff;
  color: #ff9d5c;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 2rem;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(44,62,80,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const ScrollTopButton = styled.button`
  position: fixed;
  right: 18px;
  bottom: 18px;
  background:rgb(255, 255, 255);
  color: #ff9d5c;
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(44,62,80,0.18);
  z-index: 99999;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
  &:hover {
    background: #ffb07d;
    box-shadow: 0 4px 16px rgba(44,62,80,0.22);
  }
  @media (min-width: 701px) {
    display: none;
  }
`;

const AboutFlavia: React.FC = () => {
  const isMobile = useMediaQuery('(max-width:700px)');
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const handlePhotoClick = (src: string) => {
    if (isMobile) setLightbox(src);
  };

  const handleClose = () => setLightbox(null);

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!isMobile) return;
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      // Mostrar a seta se o usuário estiver a menos de 100px do fim da página
      setShowScrollTop(scrollY + windowHeight >= docHeight - 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  return (
    <Section>
      <PawSVG width="120" height="120" viewBox="0 0 120 120" style={{top: 30, left: 0}}>
        <circle cx="30" cy="30" r="18" fill="#fff" />
        <ellipse cx="80" cy="40" rx="10" ry="16" fill="#fff" />
        <ellipse cx="40" cy="80" rx="8" ry="12" fill="#fff" />
        <ellipse cx="90" cy="90" rx="7" ry="10" fill="#fff" />
      </PawSVG>
      <PawSVG width="100" height="100" viewBox="0 0 100 100" style={{bottom: 10, right: 0, position: 'absolute'}}>
        <circle cx="25" cy="25" r="15" fill="#fff" />
        <ellipse cx="65" cy="30" rx="8" ry="13" fill="#fff" />
        <ellipse cx="35" cy="70" rx="7" ry="10" fill="#fff" />
        <ellipse cx="80" cy="80" rx="6" ry="8" fill="#fff" />
      </PawSVG>
      <ContentWrapper>
        <PhotoCluster>
          {/* Mobile: fotos em linha, centralizadas, sem sobreposição */}
          {isMobile ? (
            <>
              <div>
                <MainPhoto src="/images/flavia_1.jpg" alt="Dra. Flávia Viana sorrindo" onClick={() => handlePhotoClick('/images/flavia_1.jpg')} style={{cursor:'pointer'}} />
                
              </div>
              <div>
                <MainPhoto src="/images/flavia_2.jpg" alt="Dra. Flávia Viana em atendimento" onClick={() => handlePhotoClick('/images/flavia_2.jpg')} style={{cursor:'pointer'}} />
                
              </div>
              <div>
                <MainPhoto src="/images/flavia_3.jpg" alt="Dra. Flávia com filhote" onClick={() => handlePhotoClick('/images/flavia_3.jpg')} style={{cursor:'pointer'}} />
               
              </div>
            </>
          ) : (
            <>
              <MainPhoto src="/images/flavia_1.jpg" alt="Dra. Flávia Viana sorrindo" />
              <SmallPhoto src="/images/flavia_2.jpg" alt="Dra. Flávia Viana em atendimento" $pos="top-right" />
              <SmallPhoto src="/images/flavia_3.jpg" alt="Dra. Flávia com filhote" $pos="bottom-right" />
            </>
          )}
        </PhotoCluster>
        <InfoArea>
          <SectionTitle>Sobre a Dra. Flávia Viana</SectionTitle>
          <BadgeContainer>
            <Badge>
              <VerifiedIcon style={{ color: '#ff9d5c', fontSize: 18 }} /> Profissional de confiança
            </Badge>
          </BadgeContainer>
          <Description>
            Médica veterinária apaixonada pelo cuidado de cães e gatos, atuando sempre com carinho, dedicação e profundo respeito pela vida animal. Com uma trajetória profissional de <Highlight>mais de 10 anos</Highlight>, dedica-se a promover a saúde integral, o bem-estar e a qualidade de vida dos pets da região. Seu compromisso é oferecer um atendimento personalizado, pautado pela ética, atenção aos detalhes e profissionalismo, construindo relações sólidas de confiança com tutores e proporcionando aos animais toda a segurança e conforto que merecem.
          </Description>
        </InfoArea>
      </ContentWrapper>
      {/* Lightbox Modal */}
      {isMobile && lightbox && (
        <LightboxOverlay onClick={handleClose}>
          <LightboxImg src={lightbox} alt="Foto ampliada" onClick={e => e.stopPropagation()} />
          <CloseButton onClick={handleClose} aria-label="Fechar">&times;</CloseButton>
        </LightboxOverlay>
      )}
      {isMobile && showScrollTop && (
        <ScrollTopButton onClick={handleScrollTop} aria-label="Voltar ao topo">
          <ArrowUpwardIcon style={{ fontSize: 28 }} />
        </ScrollTopButton>
      )}
      
    </Section>
  );
};

export default AboutFlavia; 